
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { File, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import ErrorLogger from "@/utils/errorLogger";

interface FileUploaderProps {
  currentPath: string;
  onFileUploaded: () => void;
}

export const FileUploader = ({
  currentPath,
  onFileUploaded,
}: FileUploaderProps) => {
  const [isUploading, setIsUploading] = useState(false);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file size (limit to 50MB)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      toast.error("File size must be less than 50MB");
      event.target.value = "";
      return;
    }

    try {
      console.log('Starting file upload:', file.name);
      setIsUploading(true);
      
      // Get the current session to ensure user is authenticated
      const { data: sessionData } = await supabase.auth.getSession();
      
      if (!sessionData.session) {
        toast.error("You must be logged in to upload files");
        return;
      }

      // Create a unique filename with timestamp to avoid conflicts
      const timestamp = Date.now();
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const uniqueFileName = `${timestamp}_${sanitizedFileName}`;
      
      // Ensure currentPath ends with /
      const normalizedPath = currentPath.endsWith('/') ? currentPath : `${currentPath}/`;
      const filePath = `${sessionData.session.user.id}/${normalizedPath}${uniqueFileName}`;
      
      console.log(`Uploading to storage path: ${filePath}`);

      // Upload to Supabase storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("user_files")
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error("Storage upload error:", uploadError);
        throw new Error(`Upload failed: ${uploadError.message}`);
      }

      console.log('File uploaded to storage successfully:', uploadData);

      // Record in database
      const { error: dbError } = await supabase.from("file_storage").insert({
        file_name: file.name,
        file_path: filePath,
        folder_path: normalizedPath,
        size: file.size,
        mime_type: file.type || 'application/octet-stream',
        is_folder: false,
        user_id: sessionData.session.user.id
      });

      if (dbError) {
        console.error("Database record error:", dbError);
        throw new Error(`Failed to record file in database: ${dbError.message}`);
      }

      console.log('File uploaded successfully:', file.name);
      toast.success(`File "${file.name}" uploaded successfully!`);
      onFileUploaded();
    } catch (error: any) {
      console.error("Error uploading file:", error);
      const errorMessage = error.message || "Unknown error occurred during upload";
      toast.error(`Upload failed: ${errorMessage}`);
      ErrorLogger.logError(new Error(errorMessage));
    } finally {
      setIsUploading(false);
      // Reset the file input
      event.target.value = "";
    }
  };

  return (
    <Button variant="outline" asChild disabled={isUploading}>
      <label className="cursor-pointer">
        <input
          type="file"
          className="hidden"
          onChange={handleFileUpload}
          disabled={isUploading}
        />
        {isUploading ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : (
          <File className="w-4 h-4 mr-2" />
        )}
        {isUploading ? "Uploading..." : "Upload File"}
      </label>
    </Button>
  );
};
